import { useEffect, useState, useCallback } from 'react';
import {
    Building,
    Truck,
    CheckCircle,
    XCircle,
    Clock,
    Search,
    Filter,
    RefreshCw,
    Eye,
    CreditCard,
    Loader2,
    AlertCircle,
    Shield,
    Phone,
    Mail,
    Building2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { StatusBadge } from '../../components/ui/status-badge';
import { Input } from '../../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { BankVerificationAPI, BankVerificationEntity } from '../../lib/api';
import { toast } from 'sonner';

type EntityFilter = 'all' | 'supplier' | 'transporter';

export default function BankVerificationPage() {
    const [loading, setLoading] = useState(true);
    const [entities, setEntities] = useState<BankVerificationEntity[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [entityFilter, setEntityFilter] = useState<EntityFilter>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Summary stats
    const [summary, setSummary] = useState({ supplier: { count: 0 }, transporter: { count: 0 }, total: 0 });

    // Modals
    const [selectedEntity, setSelectedEntity] = useState<BankVerificationEntity | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);

    // Form state
    const [actionNotes, setActionNotes] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const fetchEntities = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Use the dedicated Bank Verification API
            const response = await BankVerificationAPI.getPendingVerifications({
                entityType: entityFilter === 'all' ? undefined : entityFilter,
                limit: 100,
            });

            if (response.success && response.data) {
                let verifications = response.data.verifications || [];

                // Apply search filter client-side
                if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    verifications = verifications.filter(e =>
                        e.entityName?.toLowerCase().includes(query) ||
                        e.email?.toLowerCase().includes(query) ||
                        e.ownerName?.toLowerCase().includes(query)
                    );
                }

                setEntities(verifications);
                setSummary(response.data.summary || { supplier: { count: 0 }, transporter: { count: 0 }, total: 0 });
            } else {
                setEntities([]);
                setSummary({ supplier: { count: 0 }, transporter: { count: 0 }, total: 0 });
            }
        } catch (err: any) {
            console.error('Error fetching bank verifications:', err);
            setError(err.message || 'Failed to fetch bank verifications');
        } finally {
            setLoading(false);
        }
    }, [entityFilter, searchQuery]);

    useEffect(() => {
        fetchEntities();
    }, [fetchEntities]);

    const handleSearch = () => {
        fetchEntities();
    };

    const handleApprove = async () => {
        if (!selectedEntity) return;

        try {
            setActionLoading(true);

            // Use the dedicated Bank Verification API
            await BankVerificationAPI.verify(
                selectedEntity.entityType,
                selectedEntity._id,
                actionNotes || undefined
            );

            toast.success(`Bank details verified for ${selectedEntity.entityName}`);
            setShowApproveModal(false);
            setActionNotes('');
            fetchEntities();
        } catch (err: any) {
            toast.error(err.message || 'Failed to verify bank details');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedEntity || !rejectReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }

        try {
            setActionLoading(true);

            // Use the dedicated Bank Verification API
            await BankVerificationAPI.reject(
                selectedEntity.entityType,
                selectedEntity._id,
                rejectReason
            );

            toast.success(`Bank verification rejected for ${selectedEntity.entityName}`);
            setShowRejectModal(false);
            setRejectReason('');
            fetchEntities();
        } catch (err: any) {
            toast.error(err.message || 'Failed to reject bank verification');
        } finally {
            setActionLoading(false);
        }
    };

    const openModal = (entity: BankVerificationEntity, action: 'view' | 'approve' | 'reject') => {
        setSelectedEntity(entity);
        switch (action) {
            case 'view':
                setShowDetailModal(true);
                break;
            case 'approve':
                setShowApproveModal(true);
                break;
            case 'reject':
                setShowRejectModal(true);
                break;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <CreditCard className="h-8 w-8 text-primary" />
                        Bank Account Verification
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Review and verify bank account details submitted by suppliers and transporters
                    </p>
                </div>
                <Button variant="outline" onClick={fetchEntities} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card
                    className={`cursor-pointer transition-all hover:shadow-md ${entityFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setEntityFilter('all')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Pending</p>
                                <p className="text-3xl font-bold">{summary.total}</p>
                            </div>
                            <div className="p-3 rounded-full bg-amber-100 dark:bg-amber-900/30">
                                <Clock className="h-6 w-6 text-amber-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card
                    className={`cursor-pointer transition-all hover:shadow-md ${entityFilter === 'supplier' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setEntityFilter('supplier')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Suppliers</p>
                                <p className="text-3xl font-bold text-blue-600">{summary.supplier.count}</p>
                            </div>
                            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                                <Building className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card
                    className={`cursor-pointer transition-all hover:shadow-md ${entityFilter === 'transporter' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setEntityFilter('transporter')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Transporters</p>
                                <p className="text-3xl font-bold text-orange-600">{summary.transporter.count}</p>
                            </div>
                            <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/30">
                                <Truck className="h-6 w-6 text-orange-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by name, email, or owner name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                className="pl-10"
                            />
                        </div>
                        <Button onClick={handleSearch}>
                            <Filter className="h-4 w-4 mr-2" />
                            Search
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Entity List */}
            <Card>
                <CardHeader>
                    <CardTitle>Pending Verifications</CardTitle>
                    <CardDescription>
                        {entities.length} entities awaiting bank account verification
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            <span className="ml-3 text-muted-foreground">Loading...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
                            <p className="text-red-600 mb-4">{error}</p>
                            <Button onClick={fetchEntities}>Try Again</Button>
                        </div>
                    ) : entities.length === 0 ? (
                        <div className="text-center py-12">
                            <Shield className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
                            <p className="text-lg font-medium text-emerald-600">All Caught Up!</p>
                            <p className="text-muted-foreground mt-1">
                                No pending bank verification requests
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {entities.map((entity) => {
                                const isSupplier = entity.entityType === 'supplier';

                                return (
                                    <div
                                        key={entity._id}
                                        className={`
                                            border rounded-lg p-4 transition-all hover:shadow-md
                                            ${isSupplier ? 'border-blue-200 bg-blue-50/50 dark:bg-blue-900/10' : 'border-orange-200 bg-orange-50/50 dark:bg-orange-900/10'}
                                        `}
                                    >
                                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                            {/* Entity Info */}
                                            <div className="flex items-start gap-3 flex-1">
                                                <div className={`p-2 rounded-full ${isSupplier ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                                                    {isSupplier ? <Building className="h-5 w-5" /> : <Truck className="h-5 w-5" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h3 className="font-semibold text-lg">{entity.entityName}</h3>
                                                        <Badge variant="outline" className={`text-xs ${isSupplier ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                                                            {isSupplier ? 'Supplier' : 'Transporter'}
                                                        </Badge>
                                                        <StatusBadge status="pending_verification" />
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                                                        <span className="flex items-center gap-1">
                                                            <Mail className="h-3.5 w-3.5" />
                                                            {entity.email}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Phone className="h-3.5 w-3.5" />
                                                            {entity.mobile}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Bank Details Preview */}
                                            <div className="flex flex-col items-end gap-1 min-w-[200px] bg-white dark:bg-gray-800 p-3 rounded-lg border">
                                                <span className="text-sm font-medium flex items-center gap-1">
                                                    <Building2 className="h-3.5 w-3.5" />
                                                    {entity.bankDetails?.bankName || 'Bank Name Not Provided'}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    A/C: {entity.bankDetails?.accountNumberMasked || entity.bankDetails?.accountNumber?.slice(-4) || 'N/A'}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    IFSC: {entity.bankDetails?.ifscCode || 'N/A'}
                                                </span>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-2 flex-wrap lg:flex-nowrap">
                                                <Button size="sm" variant="outline" onClick={() => openModal(entity, 'view')}>
                                                    <Eye className="h-4 w-4 mr-1" /> View
                                                </Button>
                                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openModal(entity, 'approve')}>
                                                    <CheckCircle className="h-4 w-4 mr-1" /> Approve
                                                </Button>
                                                <Button size="sm" variant="destructive" onClick={() => openModal(entity, 'reject')}>
                                                    <XCircle className="h-4 w-4 mr-1" /> Reject
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CreditCard className="h-5 w-5" />
                            Bank Account Details
                        </DialogTitle>
                        <DialogDescription>
                            Review bank details for {selectedEntity?.entityName}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedEntity && (
                        <div className="space-y-6">
                            {/* Entity Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Entity</Label>
                                    <p className="font-medium">{selectedEntity.entityName}</p>
                                    <p className="text-sm text-muted-foreground capitalize">{selectedEntity.entityType}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Contact</Label>
                                    <p className="font-medium">{selectedEntity.email}</p>
                                    <p className="text-sm text-muted-foreground">{selectedEntity.mobile}</p>
                                </div>
                            </div>

                            {/* Bank Details */}
                            <div className="bg-muted/50 rounded-lg p-4">
                                <Label className="text-muted-foreground mb-3 block">Bank Details</Label>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Account Holder:</span>
                                        <p className="font-medium">{selectedEntity.bankDetails?.accountHolderName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Bank:</span>
                                        <p className="font-medium">{selectedEntity.bankDetails?.bankName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Account Number:</span>
                                        <p className="font-medium font-mono">{selectedEntity.bankDetails?.accountNumber || selectedEntity.bankDetails?.accountNumberMasked || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">IFSC Code:</span>
                                        <p className="font-medium font-mono">{selectedEntity.bankDetails?.ifscCode || 'N/A'}</p>
                                    </div>
                                    {selectedEntity.bankDetails?.branchName && (
                                        <div>
                                            <span className="text-muted-foreground">Branch:</span>
                                            <p className="font-medium">{selectedEntity.bankDetails.branchName}</p>
                                        </div>
                                    )}
                                    {selectedEntity.bankDetails?.accountType && (
                                        <div>
                                            <span className="text-muted-foreground">Account Type:</span>
                                            <p className="font-medium capitalize">{selectedEntity.bankDetails.accountType}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* UPI Details if available */}
                            {selectedEntity.upiDetails?.upiId && (
                                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                                    <Label className="text-purple-700 dark:text-purple-400 mb-2 block">UPI Details</Label>
                                    <p className="font-mono font-medium">{selectedEntity.upiDetails.upiId}</p>
                                    <StatusBadge
                                        status={selectedEntity.upiDetails.isVerified ? 'verified' : 'unverified'}
                                        label={selectedEntity.upiDetails.isVerified ? 'Verified' : 'Not Verified'}
                                    />
                                </div>
                            )}

                            {/* Verification Checklist */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                                <Label className="text-amber-700 dark:text-amber-400 mb-2 block">Verification Checklist</Label>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="c1" className="rounded" />
                                        <label htmlFor="c1">Account holder name matches entity name</label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="c2" className="rounded" />
                                        <label htmlFor="c2">IFSC code is valid</label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="c3" className="rounded" />
                                        <label htmlFor="c3">Bank account number format is correct</label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="c4" className="rounded" />
                                        <label htmlFor="c4">Supporting documents verified (if applicable)</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                            Close
                        </Button>
                        <Button variant="destructive" onClick={() => {
                            setShowDetailModal(false);
                            setShowRejectModal(true);
                        }}>
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => {
                            setShowDetailModal(false);
                            setShowApproveModal(true);
                        }}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approve Modal */}
            <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-emerald-600">
                            <CheckCircle className="h-5 w-5" />
                            Approve Bank Verification
                        </DialogTitle>
                        <DialogDescription>
                            Verify the bank details for {selectedEntity?.entityName}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                            <p className="text-sm text-muted-foreground">Bank Account</p>
                            <p className="font-semibold">{selectedEntity?.bankDetails?.bankName}</p>
                            <p className="text-sm text-muted-foreground">
                                {selectedEntity?.bankDetails?.accountNumberMasked || (selectedEntity?.bankDetails?.accountNumber ? `****${selectedEntity.bankDetails.accountNumber.slice(-4)}` : 'N/A')}
                            </p>
                        </div>

                        <div>
                            <Label>Notes (Optional)</Label>
                            <Textarea
                                placeholder="Add any notes about this approval..."
                                value={actionNotes}
                                onChange={(e) => setActionNotes(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowApproveModal(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={actionLoading}>
                            {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                            Approve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Modal */}
            <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <XCircle className="h-5 w-5" />
                            Reject Bank Verification
                        </DialogTitle>
                        <DialogDescription>
                            Reject the bank details for {selectedEntity?.entityName}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                            <p className="text-sm text-muted-foreground">Bank Account</p>
                            <p className="font-semibold">{selectedEntity?.bankDetails?.bankName}</p>
                            <p className="text-sm text-muted-foreground">
                                {selectedEntity?.bankDetails?.accountNumberMasked || (selectedEntity?.bankDetails?.accountNumber ? `****${selectedEntity.bankDetails.accountNumber.slice(-4)}` : 'N/A')}
                            </p>
                        </div>

                        <div>
                            <Label>Rejection Reason (Required)</Label>
                            <Textarea
                                placeholder="Provide a clear reason for rejection..."
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={3}
                                className="border-red-200 focus:border-red-400"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRejectModal(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}>
                            {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                            Reject
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
